// InvoiceFilterOffCanvas.tsx - Modern Invoice Filter Modal Component
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Modal, Form, Button, Dropdown, Row, Col } from 'react-bootstrap';
import { 
  FaMagnifyingGlass, 
  FaHourglassHalf, 
  FaCircleCheck, 
  FaCircleXmark, 
  FaCalendarDays, 
  FaTruck,
  FaDollarSign
} from 'react-icons/fa6';
import DateFilter from '../FilterBar/components/DateFilter/DateFilter';
import { InvoiceFilters, InvoiceRecord } from '../../types';
import './InvoiceFilterOffCanvas.scss';

interface InvoiceFilterOffCanvasProps {
  show: boolean;
  onHide: () => void;
  invoices: InvoiceRecord[];
  filters: InvoiceFilters;
  onFilterChange: (filters: InvoiceFilters) => void;
  statusCounts: { pending: number; approved: number; rejected: number };
}

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const InvoiceFilterOffCanvas: React.FC<InvoiceFilterOffCanvasProps> = ({
  show,
  onHide,
  invoices,
  filters,
  onFilterChange,
  statusCounts,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Build unique provider list
  const providersList = useMemo(() => {
    const map = new Map<number, string>();
    invoices.forEach(i => {
      if (i.provider_id != null && i.provider_name) {
        map.set(i.provider_id, i.provider_name);
      }
    });
    return [
      { id: 0, name: 'Todos los proveedores' },
      ...Array.from(map.entries()).map(([id, name]) => ({ id, name })),
    ];
  }, [invoices]);

  const handleFilterUpdate = (field: keyof InvoiceFilters, value: any) => {
    onFilterChange({ ...filters, [field]: value });
  };

  const clearAllFilters = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    onFilterChange({
      searchText: '',
      startDate: formatDateForInput(thirtyDaysAgo),
      endDate: formatDateForInput(tomorrow),
      minTotal: 0,
      maxTotal: Infinity,
      providerId: 0,
      status: 'pending',
    });
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'pending':
        return { icon: FaHourglassHalf, text: 'Pendientes', className: 'pending' };
      case 'approved':
        return { icon: FaCircleCheck, text: 'Aprobadas', className: 'approved' };
      case 'rejected':
        return { icon: FaCircleXmark, text: 'Rechazadas', className: 'rejected' };
      default:
        return { icon: FaHourglassHalf, text: 'Pendientes', className: 'pending' };
    }
  };

  const statusDisplay = getStatusDisplay(filters.status);
  const StatusIcon = statusDisplay.icon;

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      fullscreen="md-down"
      centered
      size="lg"
      className="invoice-filter-offcanvas"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Filtros de Facturas
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="invoice-filter-container">
          
          {/* Search Section */}
          <div className="filter-section">
            <div className="filter-section-title">Búsqueda</div>
            <div className="mp-formgroup">
              <Form.Label className="form-label">Buscar facturas</Form.Label>
              <div className="search-input-wrapper">
                <FaMagnifyingGlass className="search-icon" />
                <Form.Control
                  ref={searchInputRef}
                  type="text"
                  placeholder="Facturas, proveedores, totales..."
                  value={filters.searchText}
                  onChange={(e) => handleFilterUpdate('searchText', e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="filter-section">
            <div className="filter-section-title">Estado</div>
            <div className="mp-formgroup">
              <Form.Label className="form-label">Estado de factura</Form.Label>
              <Dropdown className="status-dropdown">
                <Dropdown.Toggle variant="none" className="status-toggle">
                  <div className="status-display">
                    <StatusIcon className={`status-icon ${statusDisplay.className}`} />
                    <span className="status-text">{statusDisplay.text}</span>
                    <span className="status-count">
                      {statusCounts[filters.status as keyof typeof statusCounts]}
                    </span>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="status-menu">
                  <Dropdown.Item 
                    className={`status-item ${filters.status === 'pending' ? 'active' : ''}`}
                    onClick={() => handleFilterUpdate('status', 'pending')}
                  >
                    <FaHourglassHalf className="status-icon pending" />
                    <span>Pendientes</span>
                    <span className="item-count">{statusCounts.pending}</span>
                  </Dropdown.Item>
                  <Dropdown.Item 
                    className={`status-item ${filters.status === 'approved' ? 'active' : ''}`}
                    onClick={() => handleFilterUpdate('status', 'approved')}
                  >
                    <FaCircleCheck className="status-icon approved" />
                    <span>Aprobadas</span>
                    <span className="item-count">{statusCounts.approved}</span>
                  </Dropdown.Item>
                  <Dropdown.Item 
                    className={`status-item ${filters.status === 'rejected' ? 'active' : ''}`}
                    onClick={() => handleFilterUpdate('status', 'rejected')}
                  >
                    <FaCircleXmark className="status-icon rejected" />
                    <span>Rechazadas</span>
                    <span className="item-count">{statusCounts.rejected}</span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Date Range Section */}
          <div className="filter-section">
            <div className="filter-section-title">Período</div>
            <DateFilter
              startDate={filters.startDate}
              setStartDate={(date) => handleFilterUpdate('startDate', date)}
              endDate={filters.endDate}
              setEndDate={(date) => handleFilterUpdate('endDate', date)}
            />
          </div>

          {/* Provider Section */}
          <div className="filter-section">
            <div className="filter-section-title">Proveedor</div>
            <div className="mp-formgroup">
              <Form.Label className="form-label">
                <FaTruck className="label-icon" />
                Filtrar por proveedor
              </Form.Label>
              <Dropdown className="provider-dropdown">
                <Dropdown.Toggle variant="none" className="provider-toggle">
                  <div className="provider-display">
                    <FaTruck className="provider-icon" />
                    <span className="provider-text">
                      {providersList.find(p => p.id === filters.providerId)?.name || 'Todos los proveedores'}
                    </span>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="provider-menu">
                  {providersList.map(provider => (
                    <Dropdown.Item
                      key={provider.id}
                      className={`provider-item ${provider.id === filters.providerId ? 'active' : ''}`}
                      onClick={() => handleFilterUpdate('providerId', provider.id)}
                    >
                      <FaTruck className="provider-icon" />
                      <span>{provider.name}</span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Total Range Section */}
          <div className="filter-section">
            <div className="filter-section-title">Rango de Total</div>
            <Row>
              <Col>
                <div className="mp-formgroup">
                  <Form.Label className="form-label">
                    <FaDollarSign className="label-icon" />
                    Mínimo
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={filters.minTotal === 0 ? '' : filters.minTotal}
                    onChange={(e) => handleFilterUpdate('minTotal', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </Col>
              <Col>
                <div className="mp-formgroup">
                  <Form.Label className="form-label">
                    <FaDollarSign className="label-icon" />
                    Máximo
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Sin límite"
                    value={filters.maxTotal === Infinity ? '' : filters.maxTotal}
                    onChange={(e) => handleFilterUpdate('maxTotal', parseFloat(e.target.value) || Infinity)}
                  />
                </div>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div className="filter-actions">
            <Button 
              variant="outline-secondary" 
              className="clear-filters-btn"
              onClick={clearAllFilters}
            >
              Limpiar Filtros
            </Button>
            <Button 
              variant="primary" 
              className="apply-filters-btn"
              onClick={onHide}
            >
              Aplicar Filtros
            </Button>
          </div>

        </div>
      </Modal.Body>
    </Modal>
  );
};

export default InvoiceFilterOffCanvas;